import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { updateFeatureAttribute } from '../store/slices/layersSlice';
import {
  undo,
  redo,
  selectCanUndo,
  selectCanRedo,
  selectLastEdit,
  selectNextRedo,
} from '../store/slices/attributeHistorySlice';

/**
 * Custom hook that provides undo/redo functionality for attribute edits.
 * Handles keyboard shortcuts (Ctrl+Z for undo, Ctrl+Y / Ctrl+Shift+Z for redo)
 * and exposes imperative undo/redo functions for UI buttons.
 *
 * @param enabled - Whether the hook should listen for keyboard shortcuts
 */
export function useAttributeUndoRedo(enabled: boolean = true) {
  const dispatch = useAppDispatch();
  const canUndo = useAppSelector(selectCanUndo);
  const canRedo = useAppSelector(selectCanRedo);
  const lastEdit = useAppSelector(selectLastEdit);
  const nextRedo = useAppSelector(selectNextRedo);

  const handleUndo = useCallback(() => {
    if (!canUndo || !lastEdit) return;

    // Revert the attribute to its old value
    dispatch(
      updateFeatureAttribute({
        layerId: lastEdit.layerId,
        featureId: lastEdit.featureId,
        field: lastEdit.field,
        value: lastEdit.oldValue,
      })
    );

    // Move the edit from past to future
    dispatch(undo());
  }, [dispatch, canUndo, lastEdit]);

  const handleRedo = useCallback(() => {
    if (!canRedo || !nextRedo) return;

    // Re-apply the attribute's new value
    dispatch(
      updateFeatureAttribute({
        layerId: nextRedo.layerId,
        featureId: nextRedo.featureId,
        field: nextRedo.field,
        value: nextRedo.newValue,
      })
    );

    // Move the edit from future to past
    dispatch(redo());
  }, [dispatch, canRedo, nextRedo]);

  // Keyboard shortcut handler
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const isCtrlOrMeta = event.ctrlKey || event.metaKey;

      if (!isCtrlOrMeta) return;

      // Ctrl+Z (undo)
      if (event.key === 'z' && !event.shiftKey) {
        event.preventDefault();
        handleUndo();
        return;
      }

      // Ctrl+Y or Ctrl+Shift+Z (redo)
      if (event.key === 'y' || (event.key === 'z' && event.shiftKey)) {
        event.preventDefault();
        handleRedo();
        return;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, handleUndo, handleRedo]);

  return {
    canUndo,
    canRedo,
    handleUndo,
    handleRedo,
  };
}
